import { useRef } from 'react';
import type { Order } from '@/hooks/useOrders';

const pricingLabel: Record<string, string> = {
  '1_2_year': '1-2 Year Plan',
  '3_5_year': '3-5 Year Plan',
  lifetime: 'Lifetime Access',
  price_1_2_year: '1-2 Year Plan',
  price_3_5_year: '3-5 Year Plan',
  price_lifetime: 'Lifetime Access',
};

interface InvoicePDFProps {
  order: Order;
  userName: string;
  userEmail: string;
  onClose: () => void;
}

export default function InvoicePDF({ order, userName, userEmail, onClose }: InvoicePDFProps) {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    const content = printRef.current?.innerHTML;
    if (!content) return;
    const win = window.open('', '_blank', 'width=800,height=900');
    if (!win) return;
    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Invoice ${order.id}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: #fff; color: #0f172a; }
    .invoice { max-width: 680px; margin: 0 auto; padding: 48px 40px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 24px; border-bottom: 2px solid #f1f5f9; }
    .brand { display: flex; flex-direction: column; gap: 4px; }
    .brand-badge { display: inline-block; background: #ff7f0a; color: #fff; font-size: 10px; font-weight: 800; letter-spacing: 3px; padding: 4px 10px; border-radius: 4px; width: fit-content; }
    .brand-name { font-size: 22px; font-weight: 800; color: #0f172a; }
    .invoice-meta { text-align: right; }
    .invoice-title { font-size: 28px; font-weight: 800; color: #0f172a; }
    .invoice-num { font-size: 13px; color: #64748b; margin-top: 4px; font-family: monospace; }
    .invoice-date { font-size: 12px; color: #94a3b8; margin-top: 2px; }
    .status-badge { display: inline-block; background: #f0fdf4; border: 1px solid #bbf7d0; color: #15803d; font-size: 11px; font-weight: 700; padding: 4px 12px; border-radius: 999px; margin-top: 6px; }
    .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-bottom: 36px; }
    .party-label { font-size: 10px; font-weight: 700; color: #94a3b8; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 8px; }
    .party-name { font-size: 14px; font-weight: 700; color: #0f172a; }
    .party-email { font-size: 12px; color: #64748b; margin-top: 2px; }
    .items-table { width: 100%; border-collapse: collapse; margin-bottom: 28px; }
    .items-table th { background: #f8fafc; border-bottom: 2px solid #e2e8f0; padding: 10px 14px; text-align: left; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
    .items-table td { padding: 14px; border-bottom: 1px solid #f1f5f9; font-size: 13px; color: #0f172a; }
    .items-table tr:last-child td { border-bottom: none; }
    .total-row { display: flex; justify-content: flex-end; margin-bottom: 36px; }
    .total-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px 24px; min-width: 220px; }
    .total-line { display: flex; justify-content: space-between; gap: 32px; font-size: 12px; color: #64748b; margin-bottom: 6px; }
    .total-final { display: flex; justify-content: space-between; gap: 32px; font-size: 18px; font-weight: 800; color: #0f172a; border-top: 1px solid #e2e8f0; padding-top: 10px; margin-top: 10px; }
    .total-final span:last-child { color: #ff7f0a; }
    .payment-info { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; padding: 14px 18px; margin-bottom: 36px; }
    .payment-info-title { font-size: 12px; font-weight: 700; color: #15803d; margin-bottom: 8px; }
    .payment-info-row { display: flex; justify-content: space-between; font-size: 11px; color: #166534; margin-bottom: 3px; }
    .payment-info-row span:first-child { color: #4ade80; }
    .footer { border-top: 1px solid #f1f5f9; padding-top: 20px; text-align: center; font-size: 11px; color: #94a3b8; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .invoice { padding: 24px; }
    }
  </style>
</head>
<body>
  ${content}
  <script>window.onload = function() { window.print(); }<\/script>
</body>
</html>`);
    win.document.close();
  };

  const issueDate = new Date(order.created_at);
  const isSuccess = order.status === 'paid' || order.status === 'finished' || order.status === 'confirmed';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <i className="ri-file-text-line text-brand-500" />
            <span className="font-semibold text-gray-900 text-sm">Invoice Preview</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-download-2-line" />
              Download / Print PDF
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-gray-500" />
            </button>
          </div>
        </div>

        {/* Invoice Preview */}
        <div className="overflow-y-auto flex-1 p-6 bg-gray-50">
          <div ref={printRef}>
            <div className="invoice bg-white rounded-xl shadow-sm border border-gray-100 max-w-2xl mx-auto p-10">
              {/* Header */}
              <div className="header">
                <div className="brand">
                  <div className="brand-badge">API MART</div>
                  <div className="brand-name" style={{ marginTop: '8px' }}>ApiMarts</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>apimarts.com</div>
                </div>
                <div className="invoice-meta">
                  <div className="invoice-title">INVOICE</div>
                  <div className="invoice-num">{order.id}</div>
                  <div className="invoice-date">{issueDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                  {isSuccess && <div className="status-badge">PAID</div>}
                </div>
              </div>

              {/* Parties */}
              <div className="parties">
                <div>
                  <div className="party-label">From</div>
                  <div className="party-name">ApiMarts</div>
                  <div className="party-email">support@apimarts.com</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>apimarts.com</div>
                </div>
                <div>
                  <div className="party-label">Bill To</div>
                  <div className="party-name">{userName}</div>
                  <div className="party-email">{userEmail}</div>
                </div>
              </div>

              {/* Items */}
              <table className="items-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Plan</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <div style={{ fontWeight: 700 }}>{order.product_name}</div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>API Subscription Access</div>
                    </td>
                    <td>{pricingLabel[order.pricing_type] ?? order.pricing_type}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>${Number(order.amount).toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>

              {/* Total */}
              <div className="total-row">
                <div className="total-box">
                  <div className="total-line">
                    <span>Subtotal</span>
                    <span>${Number(order.amount).toFixed(2)}</span>
                  </div>
                  <div className="total-line">
                    <span>Tax</span>
                    <span>$0.00</span>
                  </div>
                  <div className="total-final">
                    <span>Total</span>
                    <span>${Number(order.amount).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Payment info */}
              {isSuccess && (
                <div className="payment-info">
                  <div className="payment-info-title">Payment Confirmed via Cryptocurrency</div>
                  <div className="payment-info-row">
                    <span>Payment ID</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{order.payment_id || '—'}</span>
                  </div>
                  <div className="payment-info-row">
                    <span>Payment Method</span>
                    <span style={{ fontWeight: 600 }}>NOWPayments (Crypto)</span>
                  </div>
                  <div className="payment-info-row">
                    <span>Date</span>
                    <span style={{ fontWeight: 600 }}>{issueDate.toLocaleString()}</span>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="footer">
                <p>Thank you for your purchase! This is your official invoice from ApiMarts.</p>
                <p style={{ marginTop: '4px' }}>For support, contact support@apimarts.com · apimarts.com</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
